/**
 * Credential-bearing query params that must never reach the diagnostic event
 * log. SSE feeds (firehose / equities / economic-calendar) pass the Keycloak
 * access token as `?token=<JWT>`; `access_token` / `auth` / `key` are covered
 * defensively in case other callers adopt different names.
 */
const SENSITIVE_PARAMS = ["token", "access_token", "auth", "key"];

/**
 * Replace the value of any sensitive query param with `[REDACTED]` before a
 * URL is recorded in the diagnostic event log.
 *
 * The log is a ring buffer persisted to `localStorage` for 24h and bundled
 * verbatim into exported diagnostic reports (and anything shipping those
 * entries to Sentry), so a raw `?token=<JWT>` would leak a usable bearer
 * credential. The param key is kept (value masked) so the diagnostic signal
 * "a token was present" survives without exposing it.
 *
 * Never throws — diagnostic logging must not be able to break a connection.
 * Falls back to a regex strip if the string is not a parseable URL.
 */
export function redactUrl(url: string): string {
  try {
    const u = new URL(url);
    for (const param of SENSITIVE_PARAMS) {
      if (u.searchParams.has(param)) {
        u.searchParams.set(param, "[REDACTED]");
      }
    }
    return u.toString();
  } catch {
    return url.replace(
      /([?&](?:token|access_token|auth|key)=)[^&]*/gi,
      "$1[REDACTED]"
    );
  }
}
